import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

/**
 * The site switcher for multi-site groups, in the top bar.
 *
 * Shown only to somebody who can open more than one site — the account owner
 * (every site) and managers given several. Picking a site re-issues the
 * session for it and restarts the app on that site's landing page, so
 * everything on screen — clients, staff, rota, care notes — is that site's.
 * Anything created afterwards is created there.
 */
const SiteSwitcher = () => {
  const { sites, activeSiteId, switchSite } = useAuth();
  const [open, setOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!Array.isArray(sites) || sites.length < 2) return null;

  const active = sites.find((site) => Number(site.id) === Number(activeSiteId)) || sites[0];

  const choose = async (site) => {
    if (Number(site.id) === Number(active.id)) {
      setOpen(false);
      return;
    }
    setSwitchingTo(site.id);
    try {
      const response = await switchSite(site.id);
      // On success the app reloads on the new site; nothing more to do here.
      if (!response?.success) {
        toast.error(response?.message || 'Could not switch site');
        setSwitchingTo(null);
      }
    } catch (error) {
      toast.error(error?.message || 'Could not switch site');
      setSwitchingTo(null);
    }
  };

  return (
    <div className="relative min-w-0" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch site"
        className="flex items-center gap-2 h-9 pl-2.5 pr-2 rounded-lg bg-gray-50 dark:bg-[#31373f] border border-gray-100 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-900/60 transition-colors min-w-0"
      >
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shrink-0">
          <Building2 className="w-[15px] h-[15px]" />
        </span>
        <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 truncate max-w-[110px] sm:max-w-[200px]">
          {active.name}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl shadow-lg py-1.5 z-50"
        >
          <p className="px-3.5 pt-1 pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Your sites
          </p>
          {sites.map((site) => {
            const isActive = Number(site.id) === Number(active.id);
            const isSwitching = Number(switchingTo) === Number(site.id);
            return (
              <button
                key={site.id}
                type="button"
                role="option"
                aria-selected={isActive}
                disabled={switchingTo !== null}
                onClick={() => choose(site)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[13px] font-semibold transition disabled:opacity-60 ${isActive
                  ? 'text-blue-700 dark:text-blue-400 bg-blue-50/70 dark:bg-blue-900/20'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                }`}
              >
                <span className="flex-1 min-w-0 truncate">{site.name}</span>
                {isSwitching && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                {isActive && !isSwitching && <Check className="w-4 h-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SiteSwitcher;
